/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { getScheduleColumns } from "services/reportSchedulerService";

interface ColumnOption {
  id: string;
  defaultLabel: string;
}

interface SelectedColumn {
  id: string;
  customLabel?: string;
}

interface ColumnSelectorProps {
  reportType: string;
  selectedColumns: SelectedColumn[];
  onChange: (columns: SelectedColumn[]) => void;
}

const useStyles = makeStyles(() => ({
  container: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "#fafafa",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  columnRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "6px 0",
    borderBottom: "1px solid #f0f0f0",
  },
  checkboxLabel: {
    minWidth: "200px",
  },
  customLabelInput: {
    flex: 1,
    maxWidth: "300px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "24px",
  },
}));

const ColumnSelector = ({
  reportType,
  selectedColumns,
  onChange,
}: ColumnSelectorProps) => {
  const classes = useStyles();
  const [columns, setColumns] = useState<ColumnOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reportType) return;

    const fetchColumns = async () => {
      try {
        setLoading(true);
        const response = await getScheduleColumns(reportType);
        if (response?.status && response?.data) {
          setColumns(response.data || []);
        }
      } catch (error: any) {
        console.error("Error fetching columns:", error);
        setColumns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchColumns();
  }, [reportType]);

  const isSelected = (id: string) =>
    selectedColumns.some((col) => col.id === id);

  const getCustomLabel = (id: string) =>
    selectedColumns.find((col) => col.id === id)?.customLabel || "";

  const handleToggle = (id: string) => {
    if (isSelected(id)) {
      onChange(selectedColumns.filter((col) => col.id !== id));
    } else {
      onChange([...selectedColumns, { id }]);
    }
  };

  const handleCustomLabelChange = (id: string, label: string) => {
    onChange(
      selectedColumns.map((col) =>
        col.id === id ? { ...col, customLabel: label || undefined } : col,
      ),
    );
  };

  const allSelected =
    columns.length > 0 && columns.every((col) => isSelected(col.id));

  const handleToggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      // Preserve existing custom labels when selecting all
      onChange(
        columns.map((col) => {
          const existing = selectedColumns.find((s) => s.id === col.id);
          return existing ?? { id: col.id };
        }),
      );
    }
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!columns.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        No columns available for the selected report type.
      </Typography>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="subtitle2" fontWeight={600}>
          Select Columns
        </Typography>
        <Button size="small" variant="outlined" onClick={handleToggleAll}>
          {allSelected ? "Deselect All" : "Select All"}
        </Button>
      </Box>
      {columns.map((col) => (
        <Box key={col.id} className={classes.columnRow}>
          <FormControlLabel
            className={classes.checkboxLabel}
            control={
              <Checkbox
                checked={isSelected(col.id)}
                onChange={() => handleToggle(col.id)}
                size="small"
              />
            }
            label={col.defaultLabel || col.id}
          />
          {isSelected(col.id) && (
            <TextField
              className={classes.customLabelInput}
              size="small"
              placeholder={col.defaultLabel || col.id}
              label="Custom Label (optional)"
              value={getCustomLabel(col.id)}
              onChange={(e) => handleCustomLabelChange(col.id, e.target.value)}
              variant="outlined"
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default ColumnSelector;
